import { useEffect, useMemo, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import {
  faArrowRight,
  faCalendar,
  faPlay,
} from '@fortawesome/pro-regular-svg-icons'
import { useAnalyticsTrack } from '@/features/amplitude'
import { useAppDispatch, type ReduxState } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  FETCH_USER_CALL_EVENTS_KEY,
  fetchUserCallEvents,
} from '@/actions/userCallEventsActions'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { Button, IllustrationBlock, Link, Text } from '@/ui'
import { ImageBackgroundColors, TypographyStyles } from '@/styles'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import type {
  UserCallEvent,
  UserCallEventType,
} from '@/reducers/finances/userCallEventsReducer'
import CalendlyPopupModal from '@/components/CalendlyPopupModal'
import {
  CALENDLY_RESCHEDULE_URL_BASE,
  CONNECT_AND_CLARIFY_URL,
} from '@/features/onboarding/config'
import { useMarkConnectAndClarifyRegistered } from './getting-started-items/OnboardingItemCTAContent'

enum GowCardState {
  Default = 'default',
  Scheduled = 'scheduled',
  Completed = 'completed',
}

const GETTING_STARTED_VIDEO_URL =
  'https://docs.google.com/videos/d/1XOQhuAm3IHGIwIPAYA2wvDsLRHh8gZyvmjWAXyxV2as/'

const DEFAULT_COPY =
  'Watch the getting started video for additional guidance. Still have questions? Sign up for Connect & Clarify, a live, small-group Q&A session.'

const COMPLETED_COPY =
  'Thanks for joining us. Connect & Clarify runs a few times a week, so you can book another session any time. You can also rewatch the getting started video.'

const getTitle = (state: GowCardState, isConnectAndClarify: boolean) => {
  if (state === GowCardState.Completed) {
    return 'Getting started: Connect & Clarify complete'
  }
  if (state !== GowCardState.Scheduled) return 'Getting started'
  return isConnectAndClarify
    ? 'Getting started: Connect & Clarify scheduled'
    : 'Getting started: Scheduled'
}

const ILLUSTRATION_CONFIG: Record<
  GowCardState,
  { color: ImageBackgroundColors; src: string; alt: string }
> = {
  [GowCardState.Default]: {
    color: ImageBackgroundColors.YELLOW,
    src: 'https://heard-images.s3.amazonaws.com/assets/laptop-video.svg',
    alt: 'Getting started video',
  },
  [GowCardState.Scheduled]: {
    color: ImageBackgroundColors.ORANGE,
    src: 'https://heard-images.s3.amazonaws.com/assets/calendar_month_orange.svg',
    alt: 'Scheduled group onboarding call',
  },
  [GowCardState.Completed]: {
    color: ImageBackgroundColors.GREEN,
    src: 'https://heard-images.s3.amazonaws.com/assets/circle-check-mark.svg',
    alt: 'Connect & Clarify session attended',
  },
}

const CARD_EVENT_TYPES: UserCallEventType[] = [
  'onboarding',
  'connect_and_clarify',
]

const startMillis = (event: UserCallEvent) =>
  event.startTime ? DateTime.fromISO(event.startTime).toMillis() : 0

// Connect & Clarify recurs, so a user can hold several bookings at once. The
// soonest upcoming one is the one they need to join; only when nothing is
// upcoming does the most recent past booking decide what the card says.
const selectRelevantCallEvent = (
  state: ReduxState
): UserCallEvent | undefined => {
  const events = Object.values(state.userCallEvents).filter(
    (event) =>
      event.eventType &&
      CARD_EVENT_TYPES.includes(event.eventType) &&
      !event.canceledAt
  )
  if (events.length === 0) return undefined

  const nowMs = DateTime.utc().toMillis()
  const upcoming = events.filter(
    (event) =>
      isoToUTCDateTime(event.endTime ?? event.startTime ?? '').toMillis() >
      nowMs
  )

  if (upcoming.length > 0) {
    return upcoming.reduce((soonest, event) =>
      startMillis(event) < startMillis(soonest) ? event : soonest
    )
  }

  return events.reduce((latest, event) =>
    startMillis(event) > startMillis(latest) ? event : latest
  )
}

// Use endTime so the card stays in the "Scheduled" state while the call is in
// progress (so Join Call remains available), and falls back to the default
// state only once the meeting window has fully passed.
const deriveCardState = (event: UserCallEvent | undefined): GowCardState => {
  if (!event?.startTime) return GowCardState.Default
  const referenceTime = event.endTime ?? event.startTime
  if (isoToUTCDateTime(referenceTime) > DateTime.utc()) {
    return GowCardState.Scheduled
  }
  // Only Connect & Clarify gets an attendance acknowledgement. The Getting
  // Started webinar is retired, so a past booking for it falls back to default.
  return event.eventType === 'connect_and_clarify'
    ? GowCardState.Completed
    : GowCardState.Default
}

export const GowSignupPanel = () => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const markRegistered = useMarkConnectAndClarifyRegistered()
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false)
  const callEvent = useReselector(selectRelevantCallEvent)
  const isConnectAndClarify = callEvent?.eventType === 'connect_and_clarify'
  const cardState = deriveCardState(callEvent)
  const lastTrackedStateRef = useRef<GowCardState | null>(null)
  const isLoadingCallEvents = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_USER_CALL_EVENTS_KEY
  )

  useEffect(() => {
    dispatch(fetchUserCallEvents())
  }, [dispatch])

  useEffect(() => {
    if (isLoadingCallEvents) return
    if (lastTrackedStateRef.current === cardState) return
    lastTrackedStateRef.current = cardState
    track('gow panel viewed', {
      enableIncrementalTransactionSync: true,
      state: cardState,
    })
  }, [track, cardState, isLoadingCallEvents])

  const formattedMeetingTime = useMemo(() => {
    if (!callEvent?.startTime) return null
    return isoToUTCDateTime(callEvent.startTime)
      .toLocal()
      .toFormat(
        isConnectAndClarify
          ? DATE_FORMATS_LUXON.WEEKDAY_DATE_LONG_AT_TIME_TZ
          : DATE_FORMATS_LUXON.DATETIME_WEEKDAY_LONG_TZ
      )
  }, [callEvent?.startTime, isConnectAndClarify])

  const rescheduleUrl = callEvent?.rescheduleUuid
    ? `${CALENDLY_RESCHEDULE_URL_BASE}${callEvent.rescheduleUuid}`
    : null

  const handleRegisterClick = () => {
    track('gow signup clicked', {
      enableIncrementalTransactionSync: true,
      state: cardState,
    })
    markRegistered()
  }

  const handleJoinClick = () => {
    if (!callEvent?.location) return
    track('gow join call clicked', { enableIncrementalTransactionSync: true })
    window.open(callEvent.location, '_blank', 'noopener,noreferrer')
  }

  const handleRescheduleClick = () => {
    if (!rescheduleUrl) return
    track('gow reschedule clicked', { enableIncrementalTransactionSync: true })
    setRescheduleModalOpen(true)
  }

  const handleWatchVideoClick = () => {
    track('gow watch video clicked', {
      enableIncrementalTransactionSync: true,
      state: cardState,
    })
  }

  const watchVideoLink = (
    <Link
      newPage
      variant="noUnderline"
      href={GETTING_STARTED_VIDEO_URL}
      iconLeft={faPlay}
      onClick={handleWatchVideoClick}
    >
      Watch Video
    </Link>
  )

  const title = getTitle(cardState, isConnectAndClarify)

  const registerLabel =
    cardState === GowCardState.Completed ? 'Book another session' : 'Register'

  const bodyCopy = () => {
    if (cardState === GowCardState.Completed) return COMPLETED_COPY
    if (cardState !== GowCardState.Scheduled || !formattedMeetingTime) {
      return DEFAULT_COPY
    }
    if (isConnectAndClarify) {
      return (
        <>
          You&apos;re booked for Connect &amp; Clarify, a live, small-group
          Q&amp;A session, on <strong>{formattedMeetingTime}</strong>.
          Haven&apos;t watched the getting started video yet? It covers the
          basics before you join.
        </>
      )
    }
    return (
      <>
        You&apos;re booked for a live group walkthrough of Heard on{' '}
        <strong>{formattedMeetingTime}</strong>. Prefer to learn on your own
        time? Watch the getting started video instead.
      </>
    )
  }

  return (
    <div className="w-full lg:w-[260px] lg:shrink-0 lg:self-stretch">
      {rescheduleUrl && (
        <CalendlyPopupModal
          url={rescheduleUrl}
          open={rescheduleModalOpen}
          onClose={() => setRescheduleModalOpen(false)}
          onScheduled={() => setRescheduleModalOpen(false)}
        />
      )}
      <div className="bg-neutral-0 rounded-2xl py-6 flex flex-col h-full">
        <IllustrationBlock
          size="sm"
          className="mt-[-24px] h-[118px]"
          {...ILLUSTRATION_CONFIG[cardState]}
        />
        <div className="flex flex-col pt-6 gap-2 flex-1">
          <div>
            <Text
              variant={TypographyStyles.EYEBROW}
              className="text-neutral-500 text-left"
            >
              Today at Heard
            </Text>
            <Text
              variant={TypographyStyles.H3}
              className="text-neutral-900 text-left"
            >
              {title}
            </Text>
          </div>
          <Text
            variant={TypographyStyles.BODY_SM}
            className="text-neutral-500 text-left"
          >
            {bodyCopy()}
          </Text>
          <div className="mt-auto flex flex-wrap items-center gap-2">
            {cardState === GowCardState.Scheduled ? (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  iconRight={faArrowRight}
                  onClick={handleJoinClick}
                  disabled={!callEvent?.location}
                >
                  Join Call
                </Button>
                <Button asChild variant="ghost" size="sm">
                  {watchVideoLink}
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="primary" size="sm">
                  {watchVideoLink}
                </Button>
                <Button asChild variant="secondary" size="sm">
                  <Link
                    newPage
                    variant="noUnderline"
                    href={CONNECT_AND_CLARIFY_URL}
                    iconLeft={faCalendar}
                    onClick={handleRegisterClick}
                  >
                    {registerLabel}
                  </Link>
                </Button>
              </>
            )}
          </div>
          {cardState === GowCardState.Scheduled && rescheduleUrl && (
            <Button
              variant="link"
              size="xs"
              className="self-start"
              onClick={handleRescheduleClick}
            >
              Reschedule call
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
